// Notification.jsx
import React from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@material-ui/core";
import { Add, Remove } from "@material-ui/icons";
import { useIntl } from "react-intl";
import { common } from "../../messages";
import NotificationCommonFields from "../../components/NotificationCommonFields";

/**
 * Props principali:
 * - position: number (-1 per creazione)
 * - elem: object (draft/creator per creazione oppure notifica esistente)
 * - name: string (facoltativo)
 * - title: string
 * - status: "init" | undefined  ("init" = modalità creazione)
 * - disabled: boolean
 * - handleChange: (name, key, position) => (value)
 * - handleSubmit: (position, event_type, after?: () => void) => Promise
 * - consents: array
 * - imagesPath: string | null
 * - notifConfig: { events: Array<{ type, type_name, offset_type }>, consent_types?: string[] }
 */
export default function Notification({
  position,
  elem,
  name,
  title,
  status,
  disabled,
  handleChange,
  handleSubmit,
  consents,
  imagesPath,
  notifConfig,
}) {
  const intl = useIntl();

  // apertura/chiusura card (per creazione la tengo chiusa di default)
  const [open, setOpen] = React.useState(Boolean(elem?.show));
  React.useEffect(() => setOpen(Boolean(elem?.show)), [elem?.show]);

  // tipo evento selezionato (per creazione)
  const [eventType, setEventType] = React.useState("");

  // se stai editando (status !== "init"), usa il tipo dell'elemento
  const effectiveEventType =
    status === "init" ? eventType : elem?.event_type || "";

  const toggleOpen = () => {
    if (disabled) return;
    setOpen((s) => !s);
  };

  // Opzioni evento prese ESCLUSIVAMENTE dalla config; nessun fallback
  const eventOptions = Array.isArray(notifConfig?.events)
    ? notifConfig.events
    : [];

  // Calcola eventConfig cercando in notifConfig.events il type === effectiveEventType
  const eventConfig = React.useMemo(() => {
    if (!Array.isArray(notifConfig?.events) || !effectiveEventType) return null;
    return (
      notifConfig.events.find((ev) => ev?.type === effectiveEventType) || null
    );
  }, [notifConfig, effectiveEventType]);

  // CHIUSURA/SVOTAMENTO solo DOPO salvataggio riuscito
  const afterSave = React.useCallback(() => {
    // chiudo accordion e resetto la select del tipo
    setOpen(false);
    setEventType("");
    // il parent resetterà `elem` a initCreator più avanti
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    if (status === "init" && !effectiveEventType) return;
    // passo afterSave come terzo argomento: verrà invocato dopo l'API OK
    handleSubmit(position, effectiveEventType, afterSave);
  };

  return (
    <Box mb={2}>
      <Card>
        {/* Header */}
        <CardHeader
          title={title}
          action={
            <IconButton disabled={disabled} onClick={toggleOpen}>
              {open ? <Remove /> : <Add />}
            </IconButton>
          }
        />

        {/* Contenuto */}
        {open && (
          <CardContent>
            <form onSubmit={onSubmit}>
              {/* Se CREAZIONE, mostro selezione tipo evento */}
              {status === "init" && (
                <Box mb={2}>
                  <FormControl fullWidth>
                    <InputLabel id="notif-type-label">
                      Tipologia evento
                    </InputLabel>
                    <Select
                      labelId="notif-type-label"
                      id="notif-type"
                      value={eventType}
                      label="Tipologia evento"
                      onChange={(e) => setEventType(e.target.value)}
                      required
                    >
                      {eventOptions.map((ev) => (
                        <MenuItem key={ev.type} value={ev.type}>
                          {ev.type_name || ev.type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* Campi comuni (contiene anche lo switch Abilitato/Disabilitato) */}
              <NotificationCommonFields
                value={elem}
                onChange={(key, val) =>
                  handleChange(
                    name || elem?.type || "CREATOR",
                    key,
                    position
                  )(val)
                }
                consents={consents}
                imagesPath={imagesPath}
                eventConfig={eventConfig}
              />

              <Button
                variant="contained"
                fullWidth
                color="primary"
                type="submit"
                style={{ marginTop: 15 }}
                disabled={status === "init" && !effectiveEventType}
              >
                {status === "init"
                  ? intl.formatMessage(common.save)
                  : intl.formatMessage(common.update)}
              </Button>
            </form>
          </CardContent>
        )}
      </Card>
    </Box>
  );
}
